import EmptyState from 'components/EmptyState/EmptyState';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import useWindow from 'hooks/useWindow';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetAirportConditionsbyStateQuery } from 'services/weather/airports/airports.api';
import { WeatherMapUpdatingStatus } from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import './assets/currentConditions.scss';
import CurrentConditionsMapContent from './components/CurrentConditionsMapContent/CurrentConditionsMapContent';
import TemperatureLegend from './components/TemperatureLegend/TemperatureLegend';
import {
  CURRENT_CONDITIONS_QUERY_OPTIONS,
  CURRENT_CONDITIONS_REFRESH_INTERVAL_MINUTES,
  CURRENT_CONDITIONS_ROUTE,
  CURRENT_CONDITIONS_REFRESH_ERROR_TOAST_ID,
} from './config/currentConditions.config';
import { getLatestCurrentConditionObservationTime } from './utils/currentConditions.utils';

const CurrentConditions = () => {
  const { locations } = useLocation();
  const { width } = useWindow();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: conditions = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
    fulfilledTimeStamp,
  } = useGetAirportConditionsbyStateQuery(undefined, CURRENT_CONDITIONS_QUERY_OPTIONS);
  const isDesktop = width >= 992;
  const latestObservationTime = useMemo(
    () => getLatestCurrentConditionObservationTime(conditions),
    [conditions],
  );

  useEffect(() => {
    if (isError && conditions.length) {
      toast.warn('The latest update failed. Displaying the most recently available conditions.', {
        toastId: CURRENT_CONDITIONS_REFRESH_ERROR_TOAST_ID,
      });
    } else {
      toast.dismiss(CURRENT_CONDITIONS_REFRESH_ERROR_TOAST_ID);
    }

    return () => toast.dismiss(CURRENT_CONDITIONS_REFRESH_ERROR_TOAST_ID);
  }, [conditions.length, isError]);

  if (isLoading) {
    return <WidgetLoadingAnimation />;
  }

  if (isError && !conditions.length) {
    return <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />;
  }

  if (!conditions.length) {
    return (
      <EmptyState
        title="Current Conditions"
        message="No current airport weather conditions are available right now."
      />
    );
  }

  return (
    <section
      aria-label="Current weather conditions"
      className="current-conditions d-flex w-100 flex-column gap-2"
    >
      <WidgetTimeUpdate
        route={CURRENT_CONDITIONS_ROUTE}
        updateIntervalBasis={CURRENT_CONDITIONS_REFRESH_INTERVAL_MINUTES}
        updatedAt={fulfilledTimeStamp ?? null}
      />

      <div className="current-conditions-map-pane d-flex min-h-0 w-100 flex-grow-1">
        <CurrentConditionsMapContent
          center={effectiveLocation?.coords}
          conditions={conditions}
          scrollZoom={isDesktop}
        />
      </div>

      <TemperatureLegend latestObservationTime={latestObservationTime} />

      {isFetching && !isLoading && (
        <WeatherMapUpdatingStatus message="Updating current weather conditions" />
      )}
    </section>
  );
};

export default CurrentConditions;
